import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, Platform, Pressable, View } from 'react-native';
import Toast from 'react-native-root-toast';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { GET_MEDIAS, MODIFY_MEDIA, CREATE_MEDIA } from '../../../common/graphql/media';
import { POS_TYPE } from '../../../common/constants';
import { usePrevious } from '../../../common/utils/hooks';
import withMediaPicker from '../../hocs/withMediaPicker';
import Text from '../../components/text/Text';
import { ActionDock, Screen, SecondaryButton } from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import useThemedStyles from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import MediaList from './MediaList';
import Overlay from '../../components/other/Overlay';
import { useOfflineMutation } from '../../offline';
import { useOfflineQueue } from '../../offline';
import { filterRemoteUrls } from '../../offline/uploadAttachments';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { isMyDayTraining } from '../myDay/myDayTraining';

const MediaListViewRaw = ({
	captureMedia,
	capturedMediaLink,
	capturedMediaBatch,
	processingMedia,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [refetch, setRefetch] = useState(null);
	// Photo links whose image uploaded but whose photo record did not save (kept until saved).
	const [failedLinks, setFailedLinks] = useState([]);
	const [isImageUploadFailed, setIsImageUploadFailed] = useState(false);
	const [isRefreshFailed, setIsRefreshFailed] = useState(false);
	const [isCameraBlocked, setIsCameraBlocked] = useState(false);
	const isMountedRef = useRef(true);
	const navigation = useNavigation();
	const route = useRoute();
	const [processing, setProcessing] = useState(false);
	const [pendingRefetchCount, setPendingRefetchCount] = useState(0);
	const refetchInProgressRef = useRef(false);
	const lastProcessedBatchIdRef = useRef(null);
	const { isOnline, queue } = useOfflineQueue();
	const isOnlineRef = useRef(isOnline);
	isOnlineRef.current = isOnline;
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const captureContextMedia = useCallback(() => {
		if (isTraining && !isOnlineRef.current) {
			Toast.show('Reconnect to continue training');
			return;
		}
		captureMedia();
	}, [captureMedia, isTraining]);

	const {
		docketId,
		machineId,
		forcePhotoImmediately,
		machineName,
		nextStepLabel,
		photoRequired,
	} = route.params || {};
	const isMarket = typeof route.params?.isMarket === 'boolean'
		? route.params.isMarket
		: route.params?.machineType === POS_TYPE.MARKET;
	const backLabel = useBackLabel();

	const [createMedia] = useOfflineMutation(CREATE_MEDIA, {
		configKey: 'createMedia',
		disableOffline: isTraining,
	});

	const initialFilter = useMemo(() => {
		let filter = {};

		if (docketId) {
			filter = { serviceDocket: docketId };
		}
		else {
			filter = { machine: machineId };
		}

		return filter;
	}, [docketId, machineId]);

	const saveMedia = useCallback(async (link) => {
		// Training bypasses queue preparation, so only uploaded photos can be saved.
		if (isTraining && !filterRemoteUrls([link]).length) {
			Toast.show('Photo not uploaded. Check your connection and take it again.');
			return;
		}

		setProcessing(true);
		try {
			const result = await createMedia({
				variables: {
					input: {
						machine: machineId,
						serviceDocket: docketId,
						link,
						...(isTraining ? { adminTraining: true } : {}),
					},
				},
				context: {
					offlineMeta: {
						machineId,
						machineName,
					},
				},
			});

			if (isMountedRef.current) {
				setFailedLinks(links => links.filter(item => item !== link));
			}

			const isQueued = Boolean(result?.offline);
			if (isQueued) {
				Toast.show('Photo queued; will sync when back online');
			}
			else {
				Toast.show('Photo uploaded successfully');
				if (isOnline) {
					setPendingRefetchCount(count => count + 1);
				}
			}
		}
		catch (e) {
			if (isOnline) {
				Toast.show('Error when uploading the photo');
				// The image itself uploaded but the photo record was not saved:
				// keep the link so the driver can save it again.
				if (isMountedRef.current) {
					setFailedLinks(links => (links.includes(link) ? links : [...links, link]));
				}
			}
			else if (isTraining) {
				Toast.show('Reconnect to continue training');
			}
			else {
				Toast.show('Photo queued; will sync when back online');
			}
		}
		finally {
			if (isMountedRef.current) {
				setProcessing(false);
			}
		}
	}, [createMedia, machineId, docketId, isOnline, isTraining, machineName]);

	const retryFailedPhotos = useCallback(async () => {
		for (const link of failedLinks) {
			await saveMedia(link);
		}
	}, [failedLinks, saveMedia]);

	// One batch per capture session: a market capture or library pick can hold several photos.
	useEffect(() => {
		if (!capturedMediaBatch || capturedMediaBatch.id === lastProcessedBatchIdRef.current) {
			return;
		}

		lastProcessedBatchIdRef.current = capturedMediaBatch.id;
		(async () => {
			for (const link of capturedMediaBatch.links) {
				await saveMedia(link);
			}
		})();
	}, [capturedMediaBatch, saveMedia]);

	// withMediaPicker drops a photo whose image upload failed (it only shows a
	// toast): an upload that ends without a new link means "take it again".
	const linkWhenUploadStartedRef = useRef(null);
	const wasProcessingMedia = usePrevious(processingMedia);
	useEffect(() => {
		if (processingMedia && !wasProcessingMedia) {
			linkWhenUploadStartedRef.current = capturedMediaLink;
			setIsImageUploadFailed(false);
		}
		else if (!processingMedia && wasProcessingMedia) {
			setIsImageUploadFailed(capturedMediaLink === linkWhenUploadStartedRef.current);
		}
	}, [capturedMediaLink, processingMedia, wasProcessingMedia]);

	useEffect(() => () => {
		isMountedRef.current = false;
	}, []);

	useEffect(() => {
		if (!pendingRefetchCount || refetchInProgressRef.current || !isOnline || typeof refetch !== 'function') {
			return;
		}

		refetchInProgressRef.current = true;

		(async () => {
			try {
				await refetch();
				if (isMountedRef.current) {
					setIsRefreshFailed(false);
				}
			}
			catch (error) {
				// The photo is saved; only the list is out of date. Offer a refresh
				// instead of retrying in a loop.
				if (isMountedRef.current) {
					setIsRefreshFailed(true);
				}
			}
			finally {
				refetchInProgressRef.current = false;
				if (isMountedRef.current) {
					// Keep requests from photos saved while this response was in flight.
					setPendingRefetchCount(count => Math.max(0, count - pendingRefetchCount));
				}
			}
		})();
	}, [isOnline, refetch, pendingRefetchCount]);

	useEffect(() => {
		if (forcePhotoImmediately) {
			captureContextMedia();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forcePhotoImmediately]);

	// A camera turned off for Sentry in the phone settings cannot be asked for again.
	useFocusEffect(useCallback(() => {
		let isActive = true;
		ImagePicker.getCameraPermissionsAsync()
			.then((permission) => {
				if (isActive) {
					setIsCameraBlocked(permission?.status === 'denied' && permission?.canAskAgain === false);
				}
			})
			.catch(() => {});

		return () => {
			isActive = false;
		};
	}, []));

	const queuedPhotos = useMemo(() => (isTraining ? [] : queue)
		.filter(entry => entry.configKey === 'createMedia' && (docketId
			? entry.meta?.serviceDocketId === docketId
			: machineId && entry.meta?.machineId === machineId))
		.map(entry => ({
			id: entry.id,
			link: entry.meta?.localLink || entry.variables?.input?.link,
			syncTag: entry.failureCount > 0
				? { label: 'Not synced', tone: 'danger' }
				: { label: 'Saved on phone', tone: 'warning' },
		})), [docketId, isTraining, machineId, queue]);

	const previousQueuedCount = usePrevious(queuedPhotos.length);
	useEffect(() => {
		if (isOnline && previousQueuedCount > queuedPhotos.length) {
			setPendingRefetchCount(count => count + 1);
		}
	}, [isOnline, previousQueuedCount, queuedPhotos.length]);

	const posName = isMarket ? 'market' : 'machine';

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			backLabel={backLabel}
			heading={isMarket ? 'Market photos' : 'Machine photos'}
			subheading={machineName}
			banner={<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />}
			dock={(
				<ActionDock
					hint={nextStepLabel ? `After this: ${nextStepLabel}` : undefined}
					action={{ label: 'Done', onPress: () => navigation.goBack() }}
				/>
			)}
		>
			{(processing || processingMedia) && (
				<Overlay />
			)}
			{isCameraBlocked && (
				<View style={styles.notice}>
					<Text style={styles.noticeText}>Camera access is turned off for Sentry. Turn it on in your phone settings to take photos.</Text>
					{Platform.OS !== 'web' && (
						<SecondaryButton label="Open settings" icon="settings" onPress={() => Linking.openSettings()} />
					)}
				</View>
			)}
			<Pressable
				onPress={captureContextMedia}
				accessibilityRole="button"
				accessibilityLabel="Add photo"
				style={({ pressed }) => [styles.capture, pressed && styles.pressed]}
			>
				<Feather name="camera" size={28} color={colors.accent} />
				<Text style={styles.captureTitle}>Add photo</Text>
				<Text style={styles.captureHint}>Take a photo or choose one from your library</Text>
			</Pressable>
			{isRefreshFailed && (
				<View style={[styles.notice, styles.failedNotice]}>
					<Text style={styles.failedText}>Photo saved, but the list could not refresh.</Text>
					<SecondaryButton label="Refresh photos" icon="refresh-cw" onPress={() => setPendingRefetchCount(count => count + 1)} />
				</View>
			)}
			{isImageUploadFailed && !failedLinks.length && (
				<View style={[styles.notice, styles.failedNotice]}>
					<Text style={styles.failedText}>Photo not uploaded. Check your connection and take it again.</Text>
				</View>
			)}
			{failedLinks.length > 0 && (
				<View style={[styles.notice, styles.failedNotice]}>
					<Text style={styles.failedText}>
						{failedLinks.length === 1 ? 'A photo did not save.' : `${failedLinks.length} photos did not save.`}
					</Text>
					<SecondaryButton label="Try again" icon="refresh-cw" onPress={retryFailedPhotos} />
				</View>
			)}
			<MediaList
				query={GET_MEDIAS}
				modifyMutation={MODIFY_MEDIA}
				initialFilter={initialFilter}
				setRefetch={setRefetch}
				queuedPhotos={queuedPhotos}
				emptyMessage={photoRequired
					? 'At least 1 photo is required to finish this stop.'
					: `No photos of this ${posName} from this visit yet.`}
			/>
			{Boolean(machineId) && (
				<Pressable
					onPress={() => navigation.push('MachinePhotoHistoryView', { machineId, machineName, excludeDocketId: docketId, docketId })}
					accessibilityRole="link"
					style={({ pressed }) => [styles.historyLink, pressed && styles.pressed]}
				>
					<Text style={styles.historyLinkText}>{`View previous photos of this ${posName}`}</Text>
				</Pressable>
			)}
		</Screen>
	);
};

const WrappedMediaListView = withMediaPicker(MediaListViewRaw);

const MediaListView = (props) => {
	const route = useRoute();
	const routeIsMarket = route.params?.isMarket;
	const isMarket = typeof routeIsMarket === 'boolean'
		? routeIsMarket
		: route.params?.machineType === POS_TYPE.MARKET;

	return (
		<WrappedMediaListView
			fallbackToLocalUri={true}
			allowMultipleSelection={true}
			collectCapturedMediaBatch={true}
			allowMultipleCapture={isMarket}
			promptForAnotherCapture={isMarket}
			{...props}
		/>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	capture: {
		minHeight: 164,
		alignItems: 'center',
		justifyContent: 'center',
		gap: space.sm,
		padding: space.xl,
		borderRadius: radius.hero,
		borderWidth: 2,
		borderStyle: 'dashed',
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	pressed: {
		opacity: 0.8,
	},
	captureTitle: {
		...type.bodyStrong,
		color: colors.text,
	},
	captureHint: {
		...type.secondary,
		color: colors.currentSubtle,
		textAlign: 'center',
	},
	notice: {
		gap: space.md,
		padding: space.lg,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.warningBorder,
		backgroundColor: colors.warningBg,
	},
	noticeText: {
		...type.bodySmallStrong,
		color: colors.warning,
	},
	failedNotice: {
		borderColor: colors.dangerBorder,
		backgroundColor: colors.dangerBg,
	},
	failedText: {
		...type.bodySmallStrong,
		color: colors.danger,
	},
	historyLink: {
		minHeight: 44,
		justifyContent: 'center',
		alignSelf: 'flex-start',
	},
	historyLinkText: {
		...type.bodySmallStrong,
		color: colors.accent,
		textDecorationLine: 'underline',
	},
});

export default MediaListView;
